import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Signal,
  WritableSignal,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Observable, take, takeUntil } from 'rxjs';
import { AlertV2Schedule } from '@models/alerts-v2.model';
import { IntelligenceModels } from '@models/intelligence.model';
import { ScheduleModel } from '@models/schedule.model';
import { ScheduleActions } from '@states/schedule/schedule.action-types';
import { SharedActions } from '@states/shared/shared.action-types';
import { UtilsService } from '../../../../../../edge/utils.service';
import { IntelligenceResultsService } from '../../../../../../services/intelligence-results.service';
import { IntelligenceToolsService } from '../../../../../../services/intelligence-tools.service';
import { UiTimeScheduleComponent } from '../../../../../ui-kit/ui-time-schedule/ui-time-schedule.component';

/**
 * Cmd+K editor for one time schedule, opened as an overlay over the time
 * schedules list. It mirrors Database → Time schedule → Create / Edit, but owns
 * its own save: the page's save is driven by the Database shell's header button
 * through the global `pressSave$` stream, which any mounted Database page also
 * listens to.
 *
 * The overlay payload's `document` picks the write. Neither branch goes through
 * the page's `startCreateSchedule`, which forks on `scheduleState.selectedSchedule`
 * — a Database page mounted behind the modal can have that populated, so a
 * create dispatched through it could silently become an update of an unrelated
 * schedule. Dispatching `createScheduleServerCall` / `updateScheduleServerCall`
 * directly cannot be redirected by state this panel never set.
 */
@Component({
  selector: 'app-create-time-schedule-result',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslatePipe, ReactiveFormsModule, UiTimeScheduleComponent],
  templateUrl: './create-time-schedule-result.component.html',
  styleUrls: ['./create-time-schedule-result.component.scss'],
})
export class CreateTimeScheduleResultComponent {
  private readonly resultsService = inject(IntelligenceResultsService);
  private readonly intelligenceToolsService = inject(IntelligenceToolsService);
  private readonly translateService = inject(TranslateService);
  private readonly utilsService = inject(UtilsService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly store$ = inject(Store);
  private readonly actions$ = inject(Actions);

  protected readonly saving: WritableSignal<boolean> = signal<boolean>(false);

  /** Overlay payload set by time-schedule-result; null once another result is shown. */
  private readonly draft: Signal<IntelligenceModels.TimeSchedules.CreateDraft | null> = computed(
    () => {
      const result = this.resultsService.current();
      return result?.type === IntelligenceModels.TimeSchedules.PanelResultType.CreateTimeSchedule
        ? (result.data as IntelligenceModels.TimeSchedules.CreateDraft)
        : null;
    },
  );

  protected readonly nameControl: FormControl<string>;

  /**
   * The grid handed to `ui-time-schedule`, bound once and never reassigned. The
   * editor re-clones its input on every change, so feeding the emitted grid back
   * in would echo each edit straight back into the control mid-interaction.
   */
  protected readonly initialSchedule: AlertV2Schedule[];

  /** The latest grid the editor reported — what a save sends. */
  private readonly schedule: WritableSignal<AlertV2Schedule[]>;

  /**
   * The document being edited, captured at construction. Read once rather than
   * through `draft()`: the save's own `replaceCurrentWithTimeSchedules` clears
   * the overlay, which would null the signal out mid-flow.
   */
  private readonly target: ScheduleModel.ScheduleDocument | null;

  public constructor() {
    const draft = this.draft();
    this.target = draft?.document ?? null;
    this.nameControl = new FormControl<string>(draft?.name ?? '', { nonNullable: true });
    this.initialSchedule = draft?.schedule ?? IntelligenceModels.TimeSchedules.blankSchedule();
    this.schedule = signal<AlertV2Schedule[]>(this.initialSchedule);

    const saveLabel = this.target ? 'DATABASE.BUTTONS.SAVE_SCHEDULE' : 'DATABASE.BUTTONS.CREATE_SCHEDULE';
    this.resultsService.setHeaderActions([
      {
        kind: IntelligenceModels.HeaderActionKind.BUTTON_DEFAULT,
        label: 'COMMON.CANCEL',
        tooltip: 'COMMON.CANCEL',
        onClick: () => this.cancel(),
        disabled: () => this.saving(),
      },
      {
        kind: IntelligenceModels.HeaderActionKind.BUTTON_PRIMARY,
        label: saveLabel,
        tooltip: saveLabel,
        onClick: () => this.save(),
        loading: () => this.saving(),
      },
    ]);
    this.destroyRef.onDestroy(() => this.resultsService.setHeaderActions([]));
  }

  protected onScheduleChanged(schedule: AlertV2Schedule[]): void {
    this.schedule.set(schedule);
  }

  private cancel(): void {
    this.resultsService.clearOverlay();
  }

  private save(): void {
    if (this.saving()) return;

    // The page saves an empty string as-is (its fallback is `??`, not `||`);
    // falling back on blank too is what the placeholder already promises.
    const name =
      this.nameControl.value.trim() ||
      this.translateService.instant('CREATE_SCHEDULE.UNTITLED');

    this.saving.set(true);
    if (this.target) this.update(this.target, name);
    else this.create(name);
  }

  private create(name: string): void {
    // The success action carries only the new id, so there is no request
    // reference to match on — a single `take(1)` is enough because this panel
    // never has two saves in flight.
    const saved$: Observable<unknown> = this.actions$.pipe(
      ofType(ScheduleActions.createScheduleServerCallSuccess),
      take(1),
    );

    // Unlike event tags, this slice has a real failure action; the effect
    // surfaces the server error as a toast, so all that is left here is
    // releasing the button for a retry.
    this.actions$
      .pipe(
        ofType(ScheduleActions.createScheduleServerCallFail),
        takeUntil(saved$),
        take(1),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.saving.set(false));

    saved$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => void this.returnToList());

    this.store$.dispatch(
      ScheduleActions.createScheduleServerCall({ schedule: { name, schedule: this.schedule() } }),
    );
  }

  /**
   * Spread the hydrated document under the edited name and grid, exactly as the
   * page's `startCreateSchedule` effect spreads `selectedSchedule` — the PATCH
   * body then carries the same fields from either surface.
   */
  private update(target: ScheduleModel.ScheduleDocument, name: string): void {
    const saved$: Observable<unknown> = this.actions$.pipe(
      ofType(ScheduleActions.updateScheduleServerCallSuccess),
      take(1),
    );

    this.actions$
      .pipe(
        ofType(ScheduleActions.updateScheduleServerCallFail),
        takeUntil(saved$),
        take(1),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.saving.set(false));

    saved$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => void this.returnToList());

    this.store$.dispatch(
      ScheduleActions.updateScheduleServerCall({
        schedule: { ...target, name, schedule: this.schedule() },
      }),
    );
  }

  /** After a successful save, swap the panel back to a refreshed schedules list. */
  private async returnToList(): Promise<void> {
    try {
      const response = await this.intelligenceToolsService.fetchTimeSchedules();
      this.resultsService.replaceCurrentWithTimeSchedules(response);
    } catch (err) {
      this.resultsService.clearOverlay();
      this.store$.dispatch(
        SharedActions.showMessage({ error: this.utilsService.errMessage(err as Error) }),
      );
    } finally {
      this.saving.set(false);
    }
  }
}
